import type {ReactElement} from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, PermissionsProvider} from '@/components';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import LimitStatusNote from '@/screens/Merchants/common/LimitStatusNote';
import BusinessLimitsTable from '@/screens/Merchants/Limit/BusinessLimitsTable';
import LimitUpdateButton from '@/screens/Merchants/Limit/LimitUpdateButton';
import {businessLimitService} from '@/services/businessLimitService';
import type {BusinessLimit} from '@/types';
import {BusinessLimitField} from '@/types';
import {logRejection} from '@/utils/logRejection';

import FeeWalletOverdraftLimitForm from './FeeWalletOverdraftLimitForm';

type BusinessLimitsTabProps = {
  businessId: string;
  dataCy?: string;
  businessName?: string;
  /** The Fee wallet limit is only meaningful for fee-wallet-approved businesses. */
  isApprovedForFeeWallet?: boolean;
  /** Undefined when the business has no override, so the global default applies. */
  feeWalletOverdraftLimitUSD?: number;
};

const BusinessLimitsTab = ({
  businessId,
  dataCy,
  businessName,
  isApprovedForFeeWallet,
  feeWalletOverdraftLimitUSD,
}: BusinessLimitsTabProps): ReactElement => {
  const [limits, setLimits] = useState<BusinessLimit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchLimits = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await businessLimitService.getBusinessLimitList();
      const allLimits = response.data || [];
      const businessLimits = allLimits.filter(
        (limit) =>
          // Business tags are stored as `COMPARATOR::value`; match the id after
          // the comparator rather than the raw tag string.
          limit.meta?.tags?.[BusinessLimitField.Business]?.split('::')[1] ===
          businessId,
      );
      setLimits(businessLimits);
    } catch (e) {
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  /* eslint-disable react-hooks/set-state-in-effect -- fetchLimits drives isLoading/error state as the fetch runs, not a render-phase set-state */
  useEffect(() => {
    // The tab filters the whole limits collection by the business id. Fetching
    // before the id is known would only render a transient, mis-filtered list
    // and force a second request once it arrives; wait for the id instead.
    if (!businessId) {
      return;
    }
    // `fetchLimits` catches into `error` state, so this only fires if that
    // catch or the finally itself throws. Log rather than swallow silently.
    fetchLimits().catch(logRejection('Failed to load business limits'));
  }, [fetchLimits, businessId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const title = useMemo(
    () => (businessName ? `Limits — ${businessName}` : 'Limits'),
    [businessName],
  );

  let tableContent: ReactElement;
  if (isLoading) {
    tableContent = <TableSkeleton />;
  } else if (error) {
    tableContent = (
      <div
        data-cy={dataCy ? `${dataCy}-error` : undefined}
        className="text-red-500"
      >
        Failed to load limits.{' '}
        <Button
          dataCy={dataCy ? `${dataCy}-retry` : undefined}
          variant="ghost"
          onClick={fetchLimits}
        >
          Retry
        </Button>
      </div>
    );
  } else if (!limits.length) {
    tableContent = (
      <p
        data-cy={dataCy ? `${dataCy}-empty` : undefined}
        className="py-10 text-center text-gray-500 dark:text-gray-400"
      >
        No limits set for {businessName || 'this merchant'} yet.
      </p>
    );
  } else {
    tableContent = (
      <BusinessLimitsTable
        items={limits}
        businessId={businessId}
        dataCy={dataCy}
      />
    );
  }

  // The overdraft limit is a fee-wallet setting, so it is only offered where
  // fee-wallet settlement is on; for everyone else it has no effect.
  const feeWalletLimitForm = isApprovedForFeeWallet ? (
    <PermissionsProvider permission={AfriexAdminPermission.BusinessLimitManage}>
      <FeeWalletOverdraftLimitForm
        businessId={businessId}
        currentLimitUSD={feeWalletOverdraftLimitUSD}
      />
    </PermissionsProvider>
  ) : null;

  return (
    <div className="space-y-4">
      {feeWalletLimitForm}

      <div className="flex items-center justify-between">
        <h3
          data-cy={dataCy ? `${dataCy}-title` : undefined}
          className="font-semibold text-gray-900 dark:text-gray-300"
        >
          {title}
        </h3>
        <PermissionsProvider
          permission={AfriexAdminPermission.BusinessLimitManage}
        >
          <LimitUpdateButton dataCy={dataCy} businessId={businessId} />
        </PermissionsProvider>
      </div>

      <LimitStatusNote inUse>
        Limit rules for this business, created here or under Merchants &gt;
        Limits. Max is the most the business can transact under a rule, in USD
        for amount rules. A once max applies to a single transaction; the daily,
        weekly and monthly totals reset at 00:00 UTC (the week resets on Monday,
        the month on the 1st). Min is the smallest single transaction an amount
        rule allows.
      </LimitStatusNote>

      {tableContent}
    </div>
  );
};

export default BusinessLimitsTab;
